import { useMemo } from "react";

export default function useAreaData({
    currentHide,
    trueWidth,
    trueHeight,
    imgDisplayHeight,
    vWidth,
    vShort,
    vLong,
    yScale,
    scale,
    localCartAreas,
    cutOption,
    isConfirmed,
    isOrderFinished,
}) {
    const rawRestrictedAreas = currentHide?.restrictedAreas;
    const rawUsedAreas = currentHide?.usedAreas;
    const rawPreSelectedAreas = currentHide?.preSelectedAreas;

    const restrictedAreas = useMemo(() => {
        try {
            const resolved = Array.isArray(rawRestrictedAreas)
                ? rawRestrictedAreas
                : (rawRestrictedAreas ? JSON.parse(rawRestrictedAreas) : []);
            return resolved.map(item => ({
                ...item,
                w: item.w || item.width || 0.05,
                h: item.h || item.height || 0.05
            }));
        } catch (e) { return []; }
    }, [rawRestrictedAreas]);

    const preSelectedAreas = useMemo(() => {
        try {
            const resolved = Array.isArray(rawPreSelectedAreas)
                ? rawPreSelectedAreas
                : (rawPreSelectedAreas ? JSON.parse(rawPreSelectedAreas) : []);
            return resolved.map(item => {
                if (item.longEnd && item.shortEnd) {
                    return {
                        ...item,
                        x: item.longEnd.x, y: item.longEnd.y,
                        w: item.longEnd.width, h: item.longEnd.height,
                    };
                }
                const w = item.w || item.width || 0;
                const h = item.h || item.height || 0;
                return {
                    ...item, w, h,
                    longEnd: { x: item.x, y: item.y, width: w, height: h },
                    shortEnd: { x: item.x, y: item.y, width: w, height: h * 0.73 },
                };
            });
        } catch (e) { return []; }
    }, [rawPreSelectedAreas]);

    const dbUsedAreas = useMemo(() => {
        try {
            const raw = Array.isArray(rawUsedAreas) ? rawUsedAreas : (rawUsedAreas ? JSON.parse(rawUsedAreas) : []);
            const LEGACY_SCALE = 4;
            const scaleRatio = (scale > 0 && LEGACY_SCALE > 0) ? scale / LEGACY_SCALE : 1;

            const confirmedSlots = preSelectedAreas
                .filter(p => p.isUsed === true)
                .flatMap(p => [
                    {
                        id: `${p.uniqueAreaId}_long`,
                        x: p.longEnd.x * trueWidth, y: p.longEnd.y * trueHeight,
                        w: p.longEnd.width * trueWidth, h: p.longEnd.height * trueHeight,
                        isFreeMove: false, isPreselected: true, endType: 'long'
                    },
                    {
                        id: `${p.uniqueAreaId}_short`,
                        x: p.shortEnd.x * trueWidth, y: p.shortEnd.y * trueHeight,
                        w: p.shortEnd.width * trueWidth, h: p.shortEnd.height * trueHeight,
                        isFreeMove: false, isPreselected: true, endType: 'short'
                    },
                ]);

            // Pre-selected leather: confirmedSlots is the canonical source (ratio-based, scale-independent).
            // normalizedRaw uses pixel coords stored at an arbitrary past scale, causing wrong positions.
            const isPreSelectedLeather = preSelectedAreas.length > 0;
            const normalizedRaw = isPreSelectedLeather ? [] : raw.map(area => ({
                ...area,
                x: area.x * scaleRatio,
                y: area.y * scaleRatio,
                w: area.w * scaleRatio,
                h: area.h * scaleRatio,
            }));

            return [...normalizedRaw, ...confirmedSlots].map(item => ({
                ...item,
                isFreeMove: item.isFreeMove === true,
                isPreselected: item.isFreeMove === false || item.isPreselected === true
            }));
        } catch (e) { return []; }
    }, [rawUsedAreas, preSelectedAreas, trueWidth, trueHeight, scale]);

    const currentLocalCartAreas = useMemo(() => {
        if (!localCartAreas || !currentHide?._id) return [];
        return localCartAreas.filter(item => item.hideId === currentHide._id);
    }, [localCartAreas, currentHide]);

    const allUsedAreas = useMemo(() => {
        const localCartIds = new Set();
        currentLocalCartAreas.forEach(item => {
            if (item.uniqueAreaId) {
                localCartIds.add(`${item.uniqueAreaId}_long`);
                localCartIds.add(`${item.uniqueAreaId}_short`);
            }
        });
        const dbAreas = dbUsedAreas.map(item => ({
            ...item,
            label: (item.id && localCartIds.has(item.id)) ? 'IN CART' : 'SOLD'
        }));
        const dbIds = new Set(dbAreas.filter(a => a.id).map(a => a.id));
        const localAreas = [];
        currentLocalCartAreas.forEach(item => {
            const toPixel = (v) => item._unit === 'mm' ? v * scale : v;
            if (item.isFreeMove && item.short && item.long) {
                localAreas.push({
                    id: `${item.uniqueAreaId}_short`,
                    x: toPixel(item.short.x), y: toPixel(item.short.y),
                    w: toPixel(item.short.w), h: toPixel(item.short.h),
                    label: 'IN CART', isFreeMove: true, endType: 'short'
                });
                localAreas.push({
                    id: `${item.uniqueAreaId}_long`,
                    x: toPixel(item.long.x), y: toPixel(item.long.y),
                    w: toPixel(item.long.w), h: toPixel(item.long.h),
                    label: 'IN CART', isFreeMove: true, endType: 'long'
                });
            } else if (item.longEnd && item.shortEnd) {
                localAreas.push({
                    id: `${item.uniqueAreaId}_short`,
                    x: toPixel(item.shortEnd.x), y: toPixel(item.shortEnd.y),
                    w: toPixel(item.shortEnd.w), h: toPixel(item.shortEnd.h),
                    label: 'IN CART', isPreselected: true, endType: 'short'
                });
                localAreas.push({
                    id: `${item.uniqueAreaId}_long`,
                    x: toPixel(item.longEnd.x), y: toPixel(item.longEnd.y),
                    w: toPixel(item.longEnd.w), h: toPixel(item.longEnd.h),
                    label: 'IN CART', isPreselected: true, endType: 'long'
                });
            } else {
                localAreas.push({
                    id: item.uniqueAreaId || `${item.hideId}_${Math.round(item.x)}_${Math.round(item.y)}`,
                    x: toPixel(item.x), y: toPixel(item.y),
                    w: toPixel(item.w), h: toPixel(item.h),
                    label: 'IN CART'
                });
            }
        });
        const dedupedLocal = localAreas.filter(a => !a.id || !dbIds.has(a.id));
        return [...dbAreas, ...dedupedLocal];
    }, [dbUsedAreas, currentLocalCartAreas, scale]);

    const hasFreeMovePurchase = useMemo(() => allUsedAreas.some(a => a.isFreeMove === true), [allUsedAreas]);
    const hasPreSelectedPurchase = useMemo(() => allUsedAreas.some(a => a.isPreselected === true), [allUsedAreas]);

    return {
        restrictedAreas, preSelectedAreas,
        dbUsedAreas, currentLocalCartAreas, allUsedAreas,
        hasFreeMovePurchase, hasPreSelectedPurchase,
    };
}
